import { useState, useRef, useCallback, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Search,
  X,
  Menu,
  LogIn,
  LogOut,
  Shield,
  UserCog,
  ExternalLink,
  PartyPopper,
  Mic2,
  BookOpen,
  Images,
  Mail,
  MapPin,
  Users,
  LayoutGrid,
} from 'lucide-react';
import axios from 'axios';
import { authAction, authSelector } from '../../features/auth/slice';
import useAuthInit from '../../hooks/useAuthInit';
import UserAvatar from '../UserAvatar';
import { useMessage } from '../../contexts/MessageContext';
import { PAPER_BASE } from '../../pages/paper/config';
import { COMPANY } from '../../data/paperCompanyInfo';

const API = process.env.REACT_APP_API_URL;

// 핑크페이퍼 도메인에서 favicon/메타 동적 변경
function usePaperMeta() {
  useEffect(() => {
    const host = window.location.hostname;
    if (host === 'pinkpaper.kr' || host === 'www.pinkpaper.kr') {
      // Favicon
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) favicon.href = '/images/paper/favicon-32.png';

      // Apple Touch Icon
      const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (appleIcon) appleIcon.href = '/images/paper/apple-touch-icon.png';

      // Theme color
      const themeColor = document.querySelector('meta[name="theme-color"]');
      if (themeColor) themeColor.content = '#0d0b14';
    }
  }, []);
}

export default function PaperLayout() {
  usePaperMeta();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { me } = useSelector(authSelector.selectAuthState);
  const { loading: authLoading } = useAuthInit();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Search state
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);
  const { unreadCount, openInbox } = useMessage() || {};

  const logout = () => {
    dispatch(authAction.logout());
    localStorage.removeItem('token');
    navigate(`${PAPER_BASE || '/'}`);
  };

  const baseNavLinks = [
    { to: `${PAPER_BASE}/festival`, label: '축제', icon: PartyPopper, gradientId: 'grad-festival' },
    { to: `${PAPER_BASE}/event`, label: '공연', icon: Mic2, gradientId: 'grad-event' },
    { to: `${PAPER_BASE}/blog`, label: '스토리', icon: BookOpen, gradientId: 'grad-blog' },
    { to: `${PAPER_BASE}/gallery`, label: '갤러리', icon: Images, gradientId: 'grad-gallery' },
  ];

  const isPaperAdmin = me?.site === 'paper' && (me?.adminYn === 'Y' || me?.superAdminYn === 'Y');
  const groupwareHref = isPaperAdmin
    ? `https://work.pinkpaper.kr?sso=${localStorage.getItem('token') || ''}`
    : 'https://work.pinkpaper.kr';
  const navLinks = [
    ...baseNavLinks,
    ...(isPaperAdmin ? [{ href: groupwareHref, label: '그룹웨어', icon: LayoutGrid, gradientId: 'grad-groupware' }] : []),
  ];

  const iconGradients = (
    <svg width="0" height="0" style={{ position: 'absolute' }}>
      <defs>
        <linearGradient id="grad-festival" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#f97316" />
        </linearGradient>
        <linearGradient id="grad-event" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
        <linearGradient id="grad-blog" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#06b6d4" />
        </linearGradient>
        <linearGradient id="grad-gallery" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f472b6" />
          <stop offset="100%" stopColor="#fb923c" />
        </linearGradient>
        <linearGradient id="grad-groupware" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#818cf8" />
        </linearGradient>
      </defs>
    </svg>
  );

  const isActive = (to) => pathname === to || pathname.startsWith(to + '/');
  const homePath = PAPER_BASE || '/';

  // ── Search ──
  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults(null); return; }
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API}/api/paperWork/search`, { params: { q } });
      setSearchResults(res.data);
    } catch {
      setSearchResults({ works: [] });
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) { setSearchResults(null); return; }
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const clearSearch = () => { setSearchValue(''); setSearchResults(null); };
  const handleSearchBlur = () => { setTimeout(() => setSearchResults(null), 200); };

  const handleResultClick = (id) => {
    clearSearch();
    navigate(`${PAPER_BASE}/work/${id}`);
  };

  const hasResults = searchResults && searchResults.works?.length > 0;

  const SearchDropdown = ({ dropUp = false }) =>
    searchLoading || hasResults || (searchResults && !hasResults) ? (
      <div className={`absolute ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'} left-0 right-0 bg-[#221a38] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50`}>
        {searchLoading ? (
          <div className="flex items-center justify-center gap-2 py-4 text-slate-400 text-sm">
            <div className="w-3 h-3 rounded-full border-2 border-violet-400 border-t-transparent animate-spin" />
            검색 중...
          </div>
        ) : !hasResults ? (
          <p className="text-center text-slate-500 text-sm py-4">결과 없음</p>
        ) : (
          <div className="p-2">
            {searchResults.works?.map((work) => (
              <button
                key={`w-${work.id}`}
                onMouseDown={() => handleResultClick(work.id)}
                className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-white/5 text-left transition-colors">
                {work.thumbnailUrl ? (
                  <img src={work.thumbnailUrl} alt={work.title} className="w-8 h-8 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded bg-violet-500/20 flex items-center justify-center shrink-0">
                    <Search size={12} className="text-violet-400" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-white text-xs font-medium truncate">{work.title}</p>
                  <p className="text-slate-500 text-[11px] truncate">
                    {work.category === 'FESTIVAL' ? '축제' : work.category === 'EVENT' ? '공연' : work.category}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    ) : null;

  return (
    <div className="min-h-dvh bg-[#0d0b14] text-white flex flex-col">
      <header className="sticky top-0 z-40 bg-[#0d0b14]/95 backdrop-blur-xl">
        {/* Row 1: Logo + Search + User */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="h-[52px] flex items-center gap-5">
            {/* Logo */}
            <Link to={homePath} className="shrink-0">
              <img
                src="/images/paper/logo_pinkpaper.png"
                alt="pinkpaper"
                className="h-7 w-auto brightness-0 invert opacity-90"
              />
            </Link>

            {/* Search bar */}
            <div className="flex-1 relative max-w-sm">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <input
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                onBlur={handleSearchBlur}
                placeholder="축제, 공연 검색"
                className="w-full pl-9 pr-8 py-[7px] bg-white/[0.05] border border-white/[0.08] rounded-full text-[13px] text-white placeholder-slate-500 focus:outline-none focus:bg-white/[0.08] focus:border-violet-500/30 transition-all"
              />
              {searchValue && (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  <X size={14} />
                </button>
              )}
              <SearchDropdown />
            </div>

            {/* Desktop User Area */}
            <div className="hidden sm:flex items-center gap-2 ml-auto shrink-0">
              {authLoading ? (
                <div className="w-2 h-2 rounded-full bg-slate-500 animate-pulse" />
              ) : me ? (
                <div className="flex items-center gap-2">
                  <UserAvatar
                    name={me.name}
                    profileImage={me.profileImage}
                    userType={me.userType || 'member'}
                    adminYn={me.adminYn}
                    size={28}
                  />
                  <span className="text-sm font-medium text-white max-w-[100px] truncate">
                    {me.name}
                  </span>
                  <button
                    title="메시지"
                    className="relative p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => openInbox?.()}>
                    <Mail size={15} className={unreadCount > 0 ? 'text-violet-400' : ''} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {me.adminYn === 'Y' && (
                    <button
                      title="관리자"
                      className="p-1.5 rounded-md text-amber-400 hover:text-amber-300 hover:bg-amber-400/10 transition-colors"
                      onClick={() => navigate(`${PAPER_BASE}/admin/works`)}>
                      <Shield size={15} />
                    </button>
                  )}
                  {me.userType !== 'client' && (
                    <button
                      title="내 정보"
                      className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                      onClick={() => navigate(`${PAPER_BASE}/mypage`)}>
                      <UserCog size={15} />
                    </button>
                  )}
                  <button
                    title="로그아웃"
                    className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                    onClick={logout}>
                    <LogOut size={15} />
                  </button>
                </div>
              ) : (
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors text-sm font-medium text-white"
                  onClick={() => navigate(`${PAPER_BASE}/login`)}>
                  <LogIn size={14} />
                  로그인
                </button>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="sm:hidden ml-auto p-2 text-slate-400 hover:text-white shrink-0"
              onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Row separator */}
        <div className="border-b border-white/[0.05]" />

        {/* SVG gradient definitions for nav icons */}
        {iconGradients}

        {/* Row 2: Desktop Navigation */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="hidden sm:flex items-center -ml-3 -mb-px">
            {navLinks.map(({ to, href, label, icon: Icon, gradientId }) => (
              href ? (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 text-slate-400 border-transparent hover:text-slate-200">
                  <Icon size={16} stroke={`url(#${gradientId})`} />
                  {label}
                </a>
              ) : (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-2 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                    isActive(to)
                      ? 'text-white border-violet-400'
                      : 'text-slate-400 border-transparent hover:text-slate-200'
                  }`}>
                  <Icon size={16} stroke={`url(#${gradientId})`} />
                  {label}
                </Link>
              )
            ))}

            {/* Right: CONTACT button */}
            <div className="flex items-center gap-2 ml-auto">
              <Link
                to={`${PAPER_BASE}/contact`}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold bg-violet-500/15 text-violet-400 hover:bg-violet-500/30 transition-colors">
                CONTACT
              </Link>
            </div>
          </nav>
        </div>

        {/* Row 2: Mobile nav (horizontal scroll) */}
        <div className="sm:hidden overflow-x-auto overflow-y-hidden scrollbar-hide">
          <div className="max-w-6xl mx-auto px-4">
            <nav className="flex items-center">
              {navLinks.map(({ to, href, label, icon: Icon, gradientId }) => (
                href ? (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 text-slate-400 border-transparent hover:text-slate-200">
                    <Icon size={16} stroke={`url(#${gradientId})`} />
                    {label}
                  </a>
                ) : (
                  <Link
                    key={to}
                    to={to}
                    className={`flex items-center gap-2 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      isActive(to)
                        ? 'text-white border-violet-400'
                        : 'text-slate-400 border-transparent hover:text-slate-200'
                    }`}>
                    <Icon size={16} stroke={`url(#${gradientId})`} />
                    {label}
                  </Link>
                )
              ))}
              <Link
                to={`${PAPER_BASE}/contact`}
                className="ml-auto px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-violet-500/15 text-violet-400 whitespace-nowrap">
                CONTACT
              </Link>
            </nav>
          </div>
        </div>

        {/* Bottom separator */}
        <div className="border-b border-white/[0.06]" />

        {/* Mobile dropdown */}
        {mobileOpen && (
          <nav className="sm:hidden border-t border-white/10 px-4 py-3 flex flex-col gap-1 bg-[#0d0b14] backdrop-blur">
            {/* Mobile user area */}
            {me ? (
              <div className="flex items-center justify-between px-3 py-3 mb-2 border-b border-white/5">
                <div className="flex items-center gap-2 min-w-0">
                  <UserAvatar
                    name={me.name}
                    profileImage={me.profileImage}
                    userType={me.userType || 'member'}
                    adminYn={me.adminYn}
                    size={28}
                  />
                  <span className="text-sm font-medium text-white truncate">{me.name}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <button
                    className="relative p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => { openInbox?.(); setMobileOpen(false); }}>
                    <Mail size={15} className={unreadCount > 0 ? 'text-violet-400' : ''} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {me.adminYn === 'Y' && (
                    <button
                      className="p-1.5 rounded-md text-amber-400 hover:text-amber-300 hover:bg-amber-400/10 transition-colors"
                      onClick={() => { navigate(`${PAPER_BASE}/admin/works`); setMobileOpen(false); }}>
                      <Shield size={15} />
                    </button>
                  )}
                  {me.userType !== 'client' && (
                    <button
                      className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                      onClick={() => { navigate(`${PAPER_BASE}/mypage`); setMobileOpen(false); }}>
                      <UserCog size={15} />
                    </button>
                  )}
                  <button
                    className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                    onClick={() => { logout(); setMobileOpen(false); }}>
                    <LogOut size={15} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-3 py-3 mb-2 border-b border-white/5">
                <button
                  className="flex items-center gap-2 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors text-sm font-medium text-white"
                  onClick={() => { navigate(`${PAPER_BASE}/login`); setMobileOpen(false); }}>
                  <LogIn size={14} />
                  로그인
                </button>
              </div>
            )}

            {/* Mobile search bar */}
            <div className="relative px-3 py-2">
              <Search size={15} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <input
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                onBlur={handleSearchBlur}
                placeholder="축제, 공연 검색"
                className="w-full pl-9 pr-8 py-[7px] bg-white/[0.05] border border-white/[0.08] rounded-full text-[13px] text-white placeholder-slate-500 focus:outline-none focus:bg-white/[0.08] focus:border-violet-500/30 transition-all"
              />
              {searchValue && (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearSearch}
                  className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  <X size={14} />
                </button>
              )}
              <SearchDropdown dropUp={false} />
            </div>
          </nav>
        )}
      </header>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="sm:hidden fixed inset-0 z-30 bg-black/30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#0a0810]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <img
                src="/images/paper/logo_pinkpaper.png"
                alt="pinkpaper"
                className="h-6 w-auto mb-3 brightness-0 invert opacity-70"
              />
              <p className="text-slate-500 text-xs leading-relaxed">
                상호: {COMPANY.name} · 대표: 김태연 외 1명
                <br />
                사업자등록번호: 688-07-02108
                <br />
                {COMPANY.address}<br className="sm:hidden" /> {COMPANY.addressDetail}
              </p>
            </div>
            <div className="text-slate-600 text-xs text-left sm:text-right leading-relaxed">
              <p className="text-sm font-bold tracking-wide text-slate-400">TEL: {COMPANY.tel}</p>
              <p>EMAIL: {COMPANY.email}</p>
              <div className="mt-3 flex items-center gap-2 sm:justify-end">
                <span className="text-slate-500 text-[11px]">패밀리사이트</span>
                <a
                  href="https://impink.co.kr"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-violet-500/15 text-violet-400 hover:bg-violet-500/30 transition-colors">
                  p!nk
                  <ExternalLink size={10} />
                </a>
              </div>
              <p className="mt-2">&copy; {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
